import React, { memo } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";

import { ADMISSION_COLORS as C } from "../constants/admissionTheme";

function formatGender(gender) {
  if (gender === "M" || gender === "Male") return "Male";
  if (gender === "F" || gender === "Female") return "Female";
  if (gender === "O" || gender === "Other" || gender === "Others") return "Other";
  return gender || "-";
}

function getReferralId(referral) {
  return referral.patient_id || referral.id;
}

// The queue is shared: OPD doctor referrals and Emergency "Admitted"
// dispositions land in the same ipd_queue/. The GET merges the full patient
// record into each row, and an emergency patient keeps patient_type
// "EMERGENCY" until the desk admits them — which is exactly the window this
// rail shows them in. Exported so every surface naming the source (card chip,
// load toast) derives it the same way.
export function referralSource(referral) {
  return referral.patient_type === "EMERGENCY" ? "Emergency" : "OPD";
}

function ReferralCard({ referral, selected, onPick }) {
  const photo = referral.photo;
  const photoSrc = photo
    ? (String(photo).startsWith("data:") ? photo : `data:image/jpeg;base64,${photo}`)
    : null;

  return (
    <Card
      component="button"
      type="button"
      onClick={() => onPick(referral)}
      sx={{
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 3,
        border: selected ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
        boxShadow: selected ? C.shadow.selected : C.shadow.card,
        bgcolor: selected ? C.selectedBg : C.cardBg,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: C.shadow.cardHover,
          transform: "translateY(-2px)",
        },
        "&:focus-visible": {
          outline: `2px solid ${C.primary}`,
          outlineOffset: 2,
        },
      }}
      aria-label={`Load referral for ${referral.name || "patient"}`}
      aria-pressed={selected}
    >
      <CardContent sx={{ p: "16px !important" }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          {photoSrc ? (
            <Avatar alt={referral.name} src={photoSrc} sx={{ width: 36, height: 36, flexShrink: 0 }} />
          ) : (
            <Avatar sx={{ width: 36, height: 36, flexShrink: 0, bgcolor: C.info.bg, color: C.info.text }}>
              <PersonIcon sx={{ fontSize: 22 }} />
            </Avatar>
          )}
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color={C.textPrimary}
            lineHeight={1.3}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {referral.name || "N/A"}
          </Typography>
          <Chip
            label={referralSource(referral)}
            size="small"
            sx={
              referralSource(referral) === "Emergency"
                ? { bgcolor: C.warning.bg, color: C.warning.text, fontWeight: 600, height: 20 }
                : { bgcolor: C.info.bg, color: C.info.text, fontWeight: 600, height: 20 }
            }
          />
        </Box>

        <Box sx={{ width: "100%" }}>
          <Typography variant="body2" color={C.textSecondary}>
            {formatGender(referral.gender)} | DOB: {referral.dob || "-"}
          </Typography>
          {getReferralId(referral) && (
            <Typography variant="body2" color={C.textMuted} mt={0.3}>
              ID: {getReferralId(referral)}
            </Typography>
          )}
          {referral.referred_doctor && (
            <Typography variant="body2" color={C.textPrimary} mt={0.3}>
              Referred To: Dr. {referral.referred_doctor}
            </Typography>
          )}
          {referral.referred_by_doctor && (
            <Typography variant="body2" sx={{ color: C.info.text, fontWeight: 500, mt: 0.5 }}>
              Ref: Dr. {referral.referred_by_doctor}
            </Typography>
          )}
          {referral.admission_details && (
            <Tooltip title={referral.admission_details} placement="top" arrow>
              <Typography
                variant="caption"
                color={C.textMuted}
                fontStyle="italic"
                display="block"
                mt={0.3}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  cursor: "default",
                }}
              >
                {referral.admission_details}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function OpdReferredRail({
  open,
  onToggle,
  onPick,
  referrals,
  loading,
  selectedReferralId,
  error,
}) {
  if (!open) {
    return (
      <Card
        sx={{
          display: { xs: "none", lg: "block" },
          position: "sticky",
          top: 64,
          width: 44,
        }}
      >
        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
          <Tooltip title="Expand Referred Patients">
            <IconButton onClick={onToggle} aria-label="Expand referred patients panel" size="small">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box textAlign="center" mt={1}>
            <Chip
              label={referrals.length}
              size="small"
              sx={{ bgcolor: C.primary, color: "#fff", fontWeight: 600, minWidth: 28, height: 24 }}
            />
            <PeopleIcon fontSize="small" color="action" sx={{ display: "block", mx: "auto", mt: 0.5 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        position: "sticky",
        top: 64,
        maxHeight: "calc(100vh - 5rem)",
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: C.shadow.card,
        border: `1px solid ${C.border}`,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: C.panelHeaderBg,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} color={C.textPrimary} sx={{ fontSize: "1rem" }}>
          Referred Patients
        </Typography>
        <Chip
          label={referrals.length}
          size="small"
          sx={{ bgcolor: C.primary, color: "#fff", fontWeight: 600, height: 24 }}
        />
        {selectedReferralId && (
          <Chip
            label={`Selected: ${referrals.find((r) => getReferralId(r) === selectedReferralId)?.name || ""}`}
            size="small"
            sx={{
              bgcolor: C.chipInfoBg,
              color: C.chipInfoText,
              fontWeight: 600,
              height: 24,
              ml: "auto",
              maxWidth: "100%",
            }}
          />
        )}
        <Tooltip title="Collapse">
          <IconButton
            onClick={onToggle}
            aria-label="Collapse referred patients panel"
            size="small"
            sx={{ ml: selectedReferralId ? 0 : "auto" }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
        }}
      >
        {loading && (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={120} />)}
          </Stack>
        )}

        {!loading && error && (
          <Typography variant="body2" color="error" textAlign="center" py={2}>
            {error}
          </Typography>
        )}

        {!loading && !error && referrals.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No referred patients in queue.
          </Typography>
        )}

        {!loading && referrals.length > 0 && (
          <Stack spacing={2} role="list" aria-label="Referred patients">
            {referrals.map((referral, index) => {
              const id = getReferralId(referral);
              return (
                <Box key={id || index} role="listitem">
                  <ReferralCard
                    referral={referral}
                    selected={selectedReferralId === id}
                    onPick={onPick}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

export default memo(OpdReferredRail);
